import { Imatge } from '@portal-internet/components';
import { getStorageValue, setStorageValue } from '@portal-internet/core';
import Boto from 'components/ui/Boto';
import { useModulAvis } from 'hooks/useModulAvis';
import { useProducte } from 'hooks/3cat/useProducte';
import { useUser } from 'hooks/useUser';
import __isUndefined from 'lodash/isUndefined';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import styles from './onboardinglogin.module.scss';

const FOCUSABLE_SELECTOR =
    'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

export default function Component(props) {
    const { limitTotalQueEsMostra } = props;
    const normalizedModalShowLimit = Number(limitTotalQueEsMostra) || 0;
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [showedToday, setShowedToday] = useState(true);
    const [cookieChecked, setCookieChecked] = useState(false);
    const [storageData, setStorageData] = useState();
    const timeoutShowOnboarding = useRef();
    const hasCountedCurrentDisplay = useRef(false);
    const previousBodyOverflow = useRef();
    const { isWebviewActive } = useProducte();
    const { isModulAvisActive } = useModulAvis();

    const router = useRouter();
    const refOnboarding = useRef();
    const isTest =
        typeof window !== 'undefined' && window.Cypress && window?.Cypress?.spec?.baseName !== 'Onboarding.test.feature';
    const { signUp, signIn, loginState, loginStateTypes } = useUser();
    const nomCookie = 'onboarding-login';
    const estructuraCookieDefault = {
        lastShown: null,
        count: 0,
        totalCount: 0
    };

    const isEmbedOrPopup = router.asPath.includes('/popup/') || router.asPath.includes('/embed/');

    const saveOnStorage = (values) => {
        setStorageValue(nomCookie, values, {
            path: '/',
            domain: '.3cat.cat',
            expires: 1
        });
    };
    const initializeValues = (data) => {
        if (__isUndefined(data)) {
            return estructuraCookieDefault;
        }

        const legacyTotalCount = Number.isFinite(Number(data?.count)) ? Number(data.count) : 0;
        const normalizedTotalCount = Number.isFinite(Number(data?.totalCount)) ? Number(data.totalCount) : legacyTotalCount;
        const legacyDateParts = typeof data?.day === 'string' ? data.day.split('/') : [];
        const legacyLastShown =
            legacyDateParts.length === 3
                ? Date.UTC(Number(legacyDateParts[0]), Number(legacyDateParts[1]) - 1, Number(legacyDateParts[2]))
                : null;

        return {
            ...estructuraCookieDefault,
            ...data,
            lastShown: Number.isFinite(Number(data?.lastShown)) ? Number(data.lastShown) : legacyLastShown,
            count: normalizedTotalCount,
            totalCount: normalizedTotalCount
        };
    };

    const hasModalShowLimit = () => normalizedModalShowLimit !== 0;

    const hasReachedModalShowLimit = (data = storageData) => {
        if (!hasModalShowLimit()) {
            return false;
        }

        return (data?.totalCount || 0) >= normalizedModalShowLimit;
    };

    const fetchDayCookieData = async () => {
        const data = await getStorageValue(nomCookie);
        let value = initializeValues(data);
        let todayDate = getToday();
        let lastShownDay = value.lastShown ? getToday(value.lastShown) : null;

        if (lastShownDay !== todayDate) {
            value.lastShown = Date.now();
            setShowedToday(false);
            saveOnStorage(value);
        }
        setStorageData(value);
        setCookieChecked(true);
    };

    const tancaOnboarding = () => {
        netejeUrlOnboarding();
        setShowOnboarding(false);
        setShowedToday(true);
    };

    const netejeUrlOnboarding = () => {
        let url = router.asPath?.split('#')[0];
        router.push(url, undefined, { scroll: false });
    };

    const getToday = (timestamp = Date.now()) => {
        const dateObj = new Date(timestamp);
        const month = dateObj.getUTCMonth() + 1;
        const day = dateObj.getUTCDate();
        const year = dateObj.getUTCFullYear();
        return year + '/' + month + '/' + day;
    };

    useEffect(() => {
        fetchDayCookieData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!__isUndefined(refOnboarding.current) && showOnboarding === true && !isTest && !showedToday) {
            refOnboarding.current.focus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showOnboarding, showedToday, isTest]);

    useEffect(() => {
        if (!showOnboarding || isTest || showedToday || isEmbedOrPopup || isModulAvisActive || !refOnboarding.current) {
            return undefined;
        }

        const handleKeyDown = (event) => {
            if (event.key !== 'Tab') {
                return;
            }

            const focusableElements = Array.from(refOnboarding.current.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
                (element) => !element.hasAttribute('disabled') && element.getAttribute('aria-hidden') !== 'true'
            );

            if (focusableElements.length === 0) {
                event.preventDefault();
                refOnboarding.current.focus();
                return;
            }

            const firstFocusableElement = focusableElements[0];
            const lastFocusableElement = focusableElements[focusableElements.length - 1];
            const activeElement = document.activeElement;

            if (event.shiftKey) {
                if (activeElement === firstFocusableElement || activeElement === refOnboarding.current) {
                    event.preventDefault();
                    lastFocusableElement.focus();
                }
                return;
            }

            if (activeElement === lastFocusableElement) {
                event.preventDefault();
                firstFocusableElement.focus();
            }
        };

        const currentOnboarding = refOnboarding.current;
        currentOnboarding.addEventListener('keydown', handleKeyDown);

        return () => {
            currentOnboarding.removeEventListener('keydown', handleKeyDown);
        };
    }, [showOnboarding, showedToday, isTest, isEmbedOrPopup, isModulAvisActive]);

    useEffect(() => {
        if (showOnboarding && !isTest && !showedToday && !isEmbedOrPopup && !isModulAvisActive) {
            previousBodyOverflow.current = document.body.style.overflow;
            document.body.style.overflow = 'hidden';

            return () => {
                document.body.style.overflow = previousBodyOverflow.current || 'auto';
            };
        }

        return undefined;
    }, [showOnboarding, showedToday, isTest, isEmbedOrPopup, isModulAvisActive]);

    useEffect(() => {
        if (loginState === loginStateTypes.GUEST && !isWebviewActive && cookieChecked && !hasReachedModalShowLimit()) {
            timeoutShowOnboarding.current = setTimeout(() => {
                setShowOnboarding(true);
            }, 3500);
        }
        return () => clearTimeout(timeoutShowOnboarding.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loginState, cookieChecked, isWebviewActive, limitTotalQueEsMostra, storageData]);

    useEffect(() => {
        if (!showOnboarding || isTest || showedToday || isEmbedOrPopup || isModulAvisActive || hasCountedCurrentDisplay.current) {
            return;
        }

        const nextStorageData = {
            ...(storageData || estructuraCookieDefault),
            count: (storageData?.totalCount || 0) + 1,
            totalCount: (storageData?.totalCount || 0) + 1
        };

        hasCountedCurrentDisplay.current = true;
        setStorageData(nextStorageData);
        saveOnStorage(nextStorageData);
    }, [showOnboarding, showedToday, isTest, isEmbedOrPopup, isModulAvisActive, storageData]);

    useEffect(() => {
        if (!showOnboarding) {
            hasCountedCurrentDisplay.current = false;
        }
    }, [showOnboarding]);

    const contentOnBoarding = () => {
        return (
            <div
                className={styles.onBoardingLogin}
                {...{
                    'data-testid': 'onboarding-login',
                    'role': 'dialog',
                    'aria-modal': 'true',
                    'aria-labelledby': 'onboarding-titol'
                }}
            >
                <div
                    className={styles.mask + ' ' + styles.onboarding}
                    onClick={() => tancaOnboarding()}
                    ref={refOnboarding}
                    tabIndex={-1}
                >
                    <div className={styles.wrapper} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.img}>
                            <div className={styles.imgTop}>
                                <Imatge
                                    src={'https://img.3cat.cat/multimedia/png/0/8/1791387006080.png'}
                                    layout='fill'
                                    objectFit='cover'
                                    alt=''
                                />
                            </div>
                            <div className={styles.imgBottom}>
                                <Imatge
                                    src={'https://img.3cat.cat/multimedia/png/4/2/1791386946124.png'}
                                    layout='fill'
                                    objectFit='cover'
                                    alt=''
                                />
                            </div>
                        </div>
                        <div className={styles.text}>
                            <h1 className={styles.title} id='onboarding-titol'>
                                Crea <span>la teva llista</span>
                                <br />i desa les teves sèries
                                <br />i programes favorits
                            </h1>
                            <p className={styles.subtitle}>
                                Amb el teu compte 3Cat pots guardar <strong>la teva llista</strong> de preferits en tots els
                                dispositius.
                            </p>
                            <div className={styles.btns}>
                                <Boto
                                    tag='button'
                                    text='Inicia sessió'
                                    estil='enllac'
                                    onClick={() => signUp()}
                                    className={`${styles.btn_inicia} ${styles.link}`}
                                    dataTestId='onboarding-inicia-sessio-button'
                                />
                                <Boto
                                    onClick={() => signIn()}
                                    tag='button'
                                    className={`${styles.btn_crea} ${styles.link}`}
                                    estil='T-onboarding'
                                    text='Crea el teu compte gratuït'
                                />
                                <Boto
                                    onClick={() => tancaOnboarding()}
                                    tag='button'
                                    className={`${styles.btn_sortir} ${styles.link}`}
                                    text='Continua com a convidat'
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    if (showOnboarding && !isTest && !showedToday && !isEmbedOrPopup && !isModulAvisActive) {
        return contentOnBoarding();
    } else {
        return null;
    }
}
